import {Component, OnChanges, Input, Output, EventEmitter, ElementRef} from '@angular/core';

import { PackageService } from '../services/package.service';
import {DefaultPackage, Package, Rule} from '../services/capd.model';

declare var $: any;

@Component({
  selector: 'package-detail',
  templateUrl: './package-detail.component.html',
  inputs: [ 'packDetail', 'changed' ]
})

export class PackageDetailComponent implements OnChanges {
  @Output() update: EventEmitter<any> = new EventEmitter();
  @Output() ruleDetail: EventEmitter<any> = new EventEmitter();
  @Output() ruleDeleted: EventEmitter<any> = new EventEmitter();
  @Output() passEvent: EventEmitter<any> = new EventEmitter();
  @Output() listChange: EventEmitter<any> = new EventEmitter();
  @Input() packDetail: DefaultPackage;

  oldPackDetail = '';
  packRuleset: Rule[];
  @Input() changed: boolean;
  watermarkText: string;

  nameError = false;
  newPak: DefaultPackage = { _id: '', name: '', version: '', state: 'Unpublished' };
  versionError = false;
  versionErrorText: string;

  constructor(private _service: PackageService,
              private _el: ElementRef) {}

  /**
   * Whenever a new package is selected, get its set of rules.
   *
   * @func ngOnChanges
   */
  ngOnChanges() {
    this.watermarkText = '<span>Loading...<br /><br /><img src="./app/css/img/linear-loader.gif"  alt=""/></span>';
    if (this.packDetail.name !== this.oldPackDetail) {
      this.oldPackDetail = this.packDetail.name;
      this.packRuleset = null;
    }
    this._service.getRuleset(this.packDetail._id).then((rulepak: Rule[]) => {
      this.watermarkText = '<span>The selected package has no rules.</span> <i class="ai-arrow-circle-right"></i>';
      this.packRuleset = rulepak.sort((a, b) => {
        if (a.triggerName && b.triggerName)
          return a.triggerName.localeCompare(b.triggerName)
        else {
          if (a.triggerName)
            console.log("Identified problem with rule: " + b._id)
          else
            console.log("Identified problem with rule: " + a._id)
        }
      });
    });

    this.updatePackVersion();
  }

  /**
   * When a rule is selected, it emits a signal to the main component to bring that rule up.
   *
   * @func selectRule
   */
  selectRule(rule: Rule) {
    this.ruleDetail.emit(rule);
  }

  /**
   * ngOnChanges is called every time the packDetail is changed; i.e, whenever you choose a new package to duplicate.
   * The big thing this does, besides set it to copy everything else about the old package,
   * is splitting the old version, incrementing the last digit by 1, and re-assembling the string.
   * This has some downsides-- for instance, it doesn't see a difference between version 1.00 and 1.0, making
   * them both 1.1-- but it works well for most cases.
   *
   * @func ngOnChanges
   */
  updatePackVersion() {
    const oldVersion = this.packDetail.version.split('.');
    oldVersion[oldVersion.length - 1] = (parseInt(oldVersion[oldVersion.length - 1], 10) + 1).toString();
    const newVersion = oldVersion.join('.');

    this.newPak.name = this.packDetail.name;
    this.newPak.version = newVersion;
    this.newPak.requiredNlpVersion = this.packDetail.requiredNlpVersion;
  }

  /**
   * Submits a duplicate package in the following way:
   * First, it gets a list of all packages
   * Then, it filters packages by matching their name to the duplicate request
   * Then, it filters that list by trying to match version
   * If it finds that package combination already exists, it returns an error
   * Otherwise, it sends the request to duplicate
   *
   * @func onSubmit
   */
  onSubmit() {
    this.versionError = false;

    this._service.getPackages()
        .then((packages: Package[]) => packages.filter(pak => pak.name === this.newPak.name)
            .filter(pak => pak.version === this.newPak.version))
        .then(packages => {
          if (packages.length > 0) {
            this.versionErrorText = 'This package version already exists';
            this.versionError = true;
          }
          else {
            this._service.duplicatePackage(this.newPak, this.packDetail)
                .then(() => {
                  this.listChange.emit(this.newPak);
                  this.onCancel();
                });
          }
        });
  }

  /**
   * This is called on every keyup in the "version" field. it removes alphabetical characters, leaving
   * only numbers and special characters.
   *
   * @func stripAlpha
   * @param {boolean} isVersion - true is the current field being edited is "Package version."
   */
  stripAlpha(isVersion: boolean) {
    if (isVersion) {
      this.newPak.version = this.newPak.version.replace(/[a-zA-Z]/g, '');
    }
    else{
      this.newPak.requiredNlpVersion = this.newPak.requiredNlpVersion.replace(/[a-zA-Z]/g, '');
    }
  }

  /**
   * When a rule is deleted, the normal delete is carried out. Then, the current pack ruleset
   * is replaced with the new list, and the module alerts its parents that it deleted a rule.
   *
   * @func deleteRule
   * @param {Rule} rule - the rule to delete.
   */
  deleteRule(rule: Rule) {
    this._service.removeRule(this.packDetail, rule).then(() =>
      this._service.getRuleset(this.packDetail._id).then((rulepak: Rule[]) => {
          this.packRuleset = rulepak;
          this.ruleDeleted.emit(null);
      })
    );
  }

  /**
   * Determines if the user has the ability to edit packages.
   *
   * @func hasPackageManagement
   * @returns true is the user can edit packages, and false otherwise.
   */
  hasPackageManagement(): boolean {
    return sessionStorage.getItem('permissions').indexOf('package-management') > 0;
  }

  /**
   * When a package is duplicated, this method recieves it and telephones it back to the parent.
   *
   * @func recieveDuplicate
   * @param {Package} event - the duplicate package.
   */
  recieveDuplicate(event) {
    this.passEvent.emit(event);
  }

  showAddModal() {
    $(this._el.nativeElement).find('#modal-add-package').show();
  }

  hideAddModal() {
    $(this._el.nativeElement).find('#modal-add-package').hide();
  }

  showDuplicateModal() {
    $(this._el.nativeElement).find('#modal-duplicate-package').show();
  }

  hideDuplicateModal() {
    $(this._el.nativeElement).find('#modal-duplicate-package').hide();
  }

  showPublishModal() {
    $(this._el.nativeElement).find('#modal-publish-package').show();
  }

  hidePublishModal() {
    $(this._el.nativeElement).find('#modal-publish-package').hide();
  }

  /**
   * Clears/resets the new package dialog on exit.
   *
   * @func onCancel
   */
  onCancel() {
    this.nameError = false;

    this.hideDuplicateModal();
    this.hidePublishModal();
  }

  /**
   * When confirmed, goes through with publishing the package and dismisses the modal.
   */
  onPublish() {
    this._service.publishPackage(this.packDetail).then(() => {
      this.listChange.emit(null);
    });

    this.hidePublishModal();
  }
}
